/**
 * Pure VAT calculation helpers — no Angular dependency, no partner/cart shape knowledge.
 *
 * This file is intentionally duplicated verbatim in both frontends:
 *   widget/src/app/services/vat-calculation.util.ts
 *   smart-widget/src/app/services/utils/vat-calculation.util.ts
 * Keep both copies byte-identical (same rule as custom-css.util.ts).
 *
 * All amounts are integer CENTS. All rates are integer HUNDREDTHS OF A PERCENT
 * (19.00 % is passed as rateHundredths = 1900). Addon custom-field-option prices are
 * NOT taxable lines — they carry no tax rate and are excluded by the caller before
 * these functions ever see them.
 */

export interface TaxableLine {
  netCents: number;
  rateHundredths: number;
}

export interface VatByRate {
  /** Percentage, e.g. `19` or `8.1` — NOT hundredths. */
  rate: number;
  amountCents: number;
}

export interface VatResult {
  byRate: VatByRate[];
  totalCents: number;
}

/**
 * Computes the VAT for a set of taxable lines, proportionally scaled down by any coupon
 * discount applied to the cart/booking.
 *
 * discountedNetCents   = D  — the net total AFTER the coupon (services + addons)
 * undiscountedNetCents = NA — the net total BEFORE the coupon (services + addons)
 * tax_i = roundHalfUp(netCents_i * rateHundredths_i * D / (10000 * NA))   // cents; 0 when NA <= 0
 *
 * Evaluated in exact integer arithmetic (BigInt), never floats — the same integer formula as
 * the backend, half-up, so both sides agree to the cent.
 */
export function calculateVat(
  lines: TaxableLine[],
  discountedNetCents: number,
  undiscountedNetCents: number
): VatResult {
  const byRateMap = new Map<number, number>();

  for (const line of lines || []) {
    if (!line || !(line.netCents > 0) || !(line.rateHundredths > 0)) {
      continue;
    }

    const taxCents = proportionalTaxCents(line, discountedNetCents, undiscountedNetCents, 10000);
    byRateMap.set(line.rateHundredths, (byRateMap.get(line.rateHundredths) || 0) + taxCents);
  }

  const byRate: VatByRate[] = Array.from(byRateMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([rateHundredths, amountCents]) => ({ rate: rateHundredths / 100, amountCents }));

  const totalCents = byRate.reduce((sum, entry) => sum + entry.amountCents, 0);

  return { byRate, totalCents };
}

/**
 * Computes the VAT already contained in gross (`inclusive`) prices, scaled down by any coupon
 * discount exactly like `calculateVat`.
 *
 * tax_i = roundHalfUp(netCents_i * rateHundredths_i * D / ((10000 + rateHundredths_i) * NA))   // cents
 *
 * Exact integer arithmetic (BigInt) — the same integer formula as the backend's contained-VAT
 * path, half-up, so both agree to the cent.
 */
export function calculateIncludedVat(
  lines: TaxableLine[],
  discountedNetCents: number,
  undiscountedNetCents: number
): VatResult {
  const byRateMap = new Map<number, number>();

  for (const line of lines || []) {
    if (!line || !(line.netCents > 0) || !(line.rateHundredths > 0)) {
      continue;
    }

    const taxCents = proportionalTaxCents(line, discountedNetCents, undiscountedNetCents, 10000 + line.rateHundredths);
    byRateMap.set(line.rateHundredths, (byRateMap.get(line.rateHundredths) || 0) + taxCents);
  }

  const byRate: VatByRate[] = Array.from(byRateMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([rateHundredths, amountCents]) => ({ rate: rateHundredths / 100, amountCents }));

  const totalCents = byRate.reduce((sum, entry) => sum + entry.amountCents, 0);

  return { byRate, totalCents };
}

/**
 * netCents * rateHundredths * D / (divisor * NA), rounded half-up in exact integer arithmetic.
 * Returns 0 when NA <= 0; a negative or non-finite D counts as 0.
 */
function proportionalTaxCents(
  line: TaxableLine,
  discountedNetCents: number,
  undiscountedNetCents: number,
  divisor: number
): number {
  if (!(undiscountedNetCents > 0)) {
    return 0;
  }

  const discounted = Number.isFinite(discountedNetCents) && discountedNetCents > 0 ? discountedNetCents : 0;
  const num = BigInt(Math.round(line.netCents)) * BigInt(Math.round(line.rateHundredths)) * BigInt(Math.round(discounted));
  const den = BigInt(Math.round(divisor)) * BigInt(Math.round(undiscountedNetCents));

  return roundHalfUpRatio(num, den);
}

/** num / den rounded half-up; BigInt division truncates, which is floor for non-negative operands. */
function roundHalfUpRatio(num: bigint, den: bigint): number {
  return Number((2n * num + den) / (2n * den));
}

/**
 * Only `plus` means the price is NET and the VAT is added on top (Bexio "Inkl. Steuer", Netto).
 * `exclude` puts no VAT on the document (Bexio "Exkl. Steuer"); `inclusive` (the default) and any
 * unset/unknown value already contain it. Neither adds anything.
 */
export function isVatAddedOnTop(vatType?: string): boolean {
  return vatType === 'plus';
}

/**
 * i18n key suffix for the VAT-type label shown next to a price, so a caller composes e.g.
 * `tax_info.plus`. An unset or unknown value reads as `inclusive`.
 */
export function vatTypeLabelKey(vatType?: string): string {
  return vatType === 'plus' || vatType === 'exclude' ? vatType : 'inclusive';
}
